import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Search, X, ArrowUpDown, RotateCw, Rss, ChevronUp } from "lucide-react";
import {
  getFeed,
  getSources,
  getStats,
  getFeedViews,
  favoriteFeedSource,
  unfavoriteFeedSource,
} from "../../../api/feed";
import { useArticleSidebar } from "../../Universal/ArticleCardDetailPanel";
import makeid from "../../Universal/Makeid";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import DateRangePicker from "../../Universal/DateRangePicker";

// Local modules
import { useDark } from "./feedTheme";
import { getTheme } from "./feedTheme";
import {
  cn,
  SORT_OPTIONS,
  FILTER_OPTIONS,
  feedViewIcon,
} from "./feedConstants";
import { Divider, IconBtn, DropUp, DropUpItem } from "./DropUp";
import { TypeBadge, TargetBadge, AptBadge } from "./Badges";
import StatsBar from "./StatsBar";
import { FeedCard, ScrollSentinel } from "./FeedCard";
import MultiSelectDropdown from "./MultiSelectDropdown";
import CreateEditViewModal from "./CreateEditViewModal";

/* ═══════════════════════════════════════════
   MainFeeds
   ═══════════════════════════════════════════ */
const MainFeeds = () => {
  const dark = useDark();
  const t = getTheme(dark);

  /* ── feed view state (from backend) ── */
  const [systemViews, setSystemViews] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [favoritedSources, setFavoritedSources] = useState([]);
  const [activeView, setActiveView] = useState(null);

  /* ── create/edit modal state ── */
  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);

  /* ── ref to avoid stale closure in loadFeedViews ── */
  const activeViewRef = useRef(null);
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  /* ── feed state ── */
  const [articleCards, setArticleCards] = useState([]);
  const [feedOffset, setFeedOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /* ── control bar state ── */
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState("newest");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortOpen, setSortOpen] = useState(false);

  /* ── sources multi-select ── */
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState(new Set());
  const [sourceSearch, setSourceSearch] = useState("");

  /* ── date range ── */
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /* ── aggregate stats from API ── */
  const [feedStats, setFeedStats] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);

  /* ── dynamic bar height ── */
  const [barHeight, setBarHeight] = useState(0);
  const controlBarRef = useRef(null);
  const searchInputRef = useRef(null);
  const feedScrollRef = useRef(null);

  /* ── Measure control bar height dynamically ── */
  useEffect(() => {
    if (!controlBarRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setBarHeight(entry.contentRect.height);
    });
    observer.observe(controlBarRef.current);
    return () => observer.disconnect();
  }, []);

  /* ── All target type keys (for modal pills) ── */
  const allTargetTypes = useMemo(() => {
    const types = new Set();
    systemViews.forEach((v) => {
      (v.target_types || []).forEach((tt) => types.add(tt));
    });
    return [...types];
  }, [systemViews]);

  /* ── Load feed views on mount ── */
  const loadFeedViews = useCallback(async () => {
    try {
      const res = await getFeedViews();
      const d = res?.data || {};
      if (d.system_views) setSystemViews(d.system_views);
      if (d.custom_views) setCustomViews(d.custom_views);
      if (d.favorited_sources) setFavoritedSources(d.favorited_sources);
      // Default to Global on first load (only if no view is set yet)
      if (!activeViewRef.current && d.system_views?.length) {
        const globalView = d.system_views.find((v) => v.key === "Global");
        if (globalView)
          setActiveView({ kind: "system", key: "Global", view: globalView });
      }
    } catch (e) {
      console.error("[FeedViews] Error:", e);
    }
  }, []);

  useEffect(() => {
    loadFeedViews();
    const handler = () => loadFeedViews();
    window.addEventListener("sidebar:refresh-feed-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-feed-views", handler);
  }, [loadFeedViews]);

  /* ── Listen for sidebar view switch events ── */
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;

      if (detail.kind === "system") {
        setActiveView({
          kind: "system",
          key: detail.view.key,
          view: detail.view,
        });
        setSelectedSources(new Set());
      } else if (detail.kind === "custom") {
        setActiveView({
          kind: "custom",
          document_id: detail.view.document_id,
          view: detail.view,
        });
        setSelectedSources(new Set());
      } else if (detail.kind === "favorited_source") {
        setActiveView({
          kind: "favorited_source",
          document_id: detail.document_id,
          source: detail.source,
        });
        setSelectedSources(new Set([detail.source]));
      }
    };
    window.addEventListener("sidebar:feed-switch-view", handler);
    return () =>
      window.removeEventListener("sidebar:feed-switch-view", handler);
  }, []);

  /* ── Listen for sidebar modal-open events ── */
  useEffect(() => {
    const handler = () => {
      setEditingView(null);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:feed-open-create-modal", handler);
    return () =>
      window.removeEventListener("sidebar:feed-open-create-modal", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const view = e.detail?.view;
      if (!view) return;
      setEditingView(view);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:feed-open-edit-modal", handler);
    return () =>
      window.removeEventListener("sidebar:feed-open-edit-modal", handler);
  }, []);

  /* ── Broadcast active view back to sidebar ── */
  useEffect(() => {
    if (activeView) {
      window.dispatchEvent(
        new CustomEvent("feed:active-view-changed", { detail: activeView }),
      );
    }
  }, [activeView]);

  const hasActiveFilter =
    activeFilter !== "all" ||
    activeSort !== "newest" ||
    searchQuery.trim() !== "" ||
    selectedSources.size > 0 ||
    dateFrom ||
    dateTo;

  /* ── Build card entries ── */
  const buildCardEntries = (data) =>
    data.map((article) => ({ id: makeid(12), article }));

  /* ── Sync interaction from sidebar back to card list ── */
  const handleInteract = useCallback((cardId, docId, state) => {
    setArticleCards((prev) =>
      prev.map((entry) =>
        entry.article.document_id === docId
          ? {
              ...entry,
              article: {
                ...entry.article,
                like: state.like,
                dislike: state.dislike,
                catalog: state.catalog,
              },
            }
          : entry,
      ),
    );
  }, []);

  /* ── Card click → sidebar + glow (shared hook) ── */
  const { activeCardId, setActiveCardId, handleCardClick } = useArticleSidebar(
    articleCards,
    handleInteract,
  );

  /* ── Build payload from active view ── */
  const buildPayload = (offset) => {
    const payload = { offset };
    payload.group = "public";

    if (activeView?.kind === "system") {
      const v = activeView.view;
      if (v.uuid_scoped) payload.uuid = true;
      if (v.target_types?.length) payload.target_types = v.target_types;
      if (v.keywords?.length) payload.keywords = v.keywords;
    } else if (activeView?.kind === "custom") {
      payload.view_document_id = activeView.view.document_id;
    } else if (activeView?.kind === "favorited_source") {
      const globalView = systemViews.find((v) => v.key === "Global");
      if (globalView?.target_types?.length)
        payload.target_types = globalView.target_types;
      payload.domains = [activeView.source];
    }

    payload.sort = activeSort === "oldest" ? 1 : -1;

    if (activeFilter === "liked") payload.filter = "thumbsup";
    else if (activeFilter === "disliked") payload.filter = "thumbsdown";
    else if (activeFilter === "cataloged") payload.filter = "cataloged";

    if (selectedSources.size > 0 && activeView?.kind !== "favorited_source") {
      payload.domains = [...selectedSources];
    }

    if (searchQuery.trim()) payload.search = searchQuery.trim();
    if (dateFrom) payload.date_from = dateFrom;
    if (dateTo) payload.date_to = dateTo;

    return payload;
  };

  /* ── Load sources on mount ── */
  useEffect(() => {
    const loadSources = async () => {
      try {
        const response = await getSources();
        const data = response?.data ?? response ?? [];
        const mapped = data.map((s) => ({
          key: s.label,
          label: s.label,
        }));
        setAvailableSources(mapped);
      } catch (e) {
        console.error("[Sources] Error loading sources:", e);
      }
    };
    loadSources();
  }, []);

  /* ── Fetch page ── */
  const fetchPage = useCallback(
    async (offset, append = false) => {
      if (!append) setIsLoading(true);
      else setIsLoadingMore(true);

      try {
        const payload = buildPayload(offset);
        console.log("[API] getFeed", payload);
        const response = await getFeed(payload);

        const data = response?.data ?? [];
        const entries = buildCardEntries(data);

        if (append) setArticleCards((prev) => [...prev, ...entries]);
        else setArticleCards(entries);

        if (data.length === 0) setHasMore(false);
        else setHasMore(true);
      } catch (e) {
        console.error("[Feed] Error:", e);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      activeView,
      searchQuery,
      activeSort,
      activeFilter,
      selectedSources,
      dateFrom,
      dateTo,
    ],
  );

  /* ── Reset on feed/filter change ── */
  useEffect(() => {
    if (!activeView) return;
    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    setActiveCardId(null);
    fetchPage(0, false);
    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  }, [activeView, activeSort, activeFilter, selectedSources, dateFrom, dateTo]);

  /* ── Fetch aggregate stats ── */
  useEffect(() => {
    if (!activeView) return;
    const loadStats = async () => {
      try {
        const payload = buildPayload(0);
        delete payload.offset;
        console.log("[API] getStats", payload);
        const response = await getStats(payload);
        const data = response?.data ?? response;
        if (data && data.total != null) {
          setFeedStats(data);
        } else {
          setFeedStats(null);
        }
      } catch (e) {
        console.error("[Stats] Error (will use local fallback):", e);
        setFeedStats(null);
      }
    };
    loadStats();
  }, [
    activeView,
    activeSort,
    activeFilter,
    selectedSources,
    dateFrom,
    dateTo,
    searchQuery,
  ]);

  /* ── Local stats fallback ── */
  const effectiveStats =
    feedStats ||
    (() => {
      if (articleCards.length === 0) return null;
      const sources = new Set();
      const types = new Set();
      const timestamps = [];
      articleCards.forEach(({ article }) => {
        try {
          sources.add(new URL(article.url).hostname.replace("www.", ""));
        } catch {}
        const tt = article.target_type || article.type;
        if (tt) types.add(tt);
        const ts = article.date || article.timestamp;
        if (ts)
          timestamps.push(
            typeof ts === "number" ? ts * 1000 : new Date(ts).getTime(),
          );
      });
      const validTs = timestamps.filter((n) => !isNaN(n));
      return {
        total: articleCards.length + (hasMore ? "+" : ""),
        sources: sources.size,
        types: types.size,
        date_from: validTs.length
          ? new Date(Math.min(...validTs)).toISOString()
          : null,
        date_to: validTs.length
          ? new Date(Math.max(...validTs)).toISOString()
          : null,
      };
    })();

  /* ── Infinite scroll ── */
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    const next = feedOffset + 1;
    setFeedOffset(next);
    fetchPage(next, true);
  }, [feedOffset, isLoadingMore, hasMore, fetchPage]);

  /* ── Search submit ── */
  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    console.log("[Search]", searchQuery);
    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    fetchPage(0, false);
    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  };

  /* ── Debounced auto-search ── */
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!activeView) return;
    searchTimerRef.current = setTimeout(() => {
      setArticleCards([]);
      setFeedOffset(0);
      setHasMore(true);
      fetchPage(0, false);
      if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
    }, 500);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery("");
    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    fetchPage(0, false);
    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  };

  /* ── Refresh ── */
  const handleRefresh = () => {
    setArticleCards([]);
    setFeedOffset(0);
    setHasMore(true);
    fetchPage(0, false);
    if (feedScrollRef.current) feedScrollRef.current.scrollTop = 0;
  };

  /* ── Clear all filters ── */
  const clearAllFilters = () => {
    setActiveSort("newest");
    setActiveFilter("all");
    setSearchQuery("");
    setSelectedSources(new Set());
    setDateFrom("");
    setDateTo("");
    const globalView = systemViews.find((v) => v.key === "Global");
    if (globalView)
      setActiveView({ kind: "system", key: "Global", view: globalView });
  };

  /* ── Handle source selection change ── */
  const handleSourcesChanged = (newSources) => {
    setSelectedSources(newSources);

    if (activeView?.kind === "favorited_source") {
      const globalView = systemViews.find((v) => v.key === "Global");
      if (globalView)
        setActiveView({ kind: "system", key: "Global", view: globalView });
    }

    if (newSources.size === 1) {
      const singleSource = [...newSources][0];
      const matchingFav = favoritedSources.find(
        (f) => f.source_value === singleSource,
      );
      if (matchingFav) {
        setActiveView({
          kind: "favorited_source",
          document_id: matchingFav.document_id,
          source: matchingFav.source_value,
        });
      }
    }
  };

  /* ── Toggle favorite source ── */
  const handleToggleFavoriteSource = async (source) => {
    const existing = favoritedSources.find(
      (f) => f.source_value === source.key,
    );
    try {
      if (existing) {
        await unfavoriteFeedSource({ document_id: existing.document_id });
      } else {
        await favoriteFeedSource({
          source_value: source.key,
          source_label: source.label,
        });
      }
      loadFeedViews();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Toggle favorite source failed:", err);
    }
  };

  /* ── Modal callbacks ── */
  const handleModalSaved = (view) => {
    setShowCreateViewModal(false);
    setEditingView(null);
    loadFeedViews();
    window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    if (view) {
      setActiveView({
        kind: "custom",
        document_id: view.document_id,
        view,
      });
    }
  };

  const resetModal = () => {
    setShowCreateViewModal(false);
    setEditingView(null);
  };

  /* ── Active view label ── */
  const activeViewLabel = useMemo(() => {
    if (!activeView) return "Feed";
    if (activeView.kind === "system") return activeView.key;
    if (activeView.kind === "custom") return activeView.view?.name || "Custom";
    if (activeView.kind === "favorited_source")
      return activeView.source || "Source";
    return "Feed";
  }, [activeView]);

  return (
    <div className="relative flex flex-col w-full h-full">
      {/* ═══════════ FLOATING CONTROL BAR ═══════════ */}
      <div
        ref={controlBarRef}
        className="absolute top-14 left-0 right-0 z-30 flex flex-col items-center pointer-events-none px-4 pt-2"
      >
        <div
          className="pointer-events-auto backdrop-blur-sm rounded-xl w-full"
          style={{
            background: t.barBg,
            border: `1px solid ${t.barBorder}`,
            boxShadow: t.barShadow,
          }}
        >
          {/* ── Row 1 ── */}
          <div className="flex items-center gap-3 px-2.5 py-3 flex-wrap">
            {/* Active view label */}
            {(() => {
              const viewIcon =
                activeView?.kind === "system"
                  ? activeView.view?.icon
                  : activeView?.kind === "custom"
                    ? activeView.view?.icon
                    : activeView?.kind === "favorited_source"
                      ? "globe"
                      : "rss";
              const viewColor =
                activeView?.kind === "system"
                  ? activeView.view?.color
                  : activeView?.kind === "custom"
                    ? activeView.view?.color
                    : activeView?.kind === "favorited_source"
                      ? "rgba(14,165,233,0.85)"
                      : undefined;
              const ActiveIcon = feedViewIcon(viewIcon);
              return (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap"
                  style={{
                    background: t.activeBg,
                    border: `1px solid ${t.activeBorder}`,
                    color: t.activeText,
                  }}
                >
                  <ActiveIcon
                    size={12}
                    style={{ flexShrink: 0, color: viewColor || t.activeText }}
                  />
                  <span style={{ fontSize: 11, fontWeight: 500 }}>
                    {activeViewLabel}
                  </span>
                </div>
              );
            })()}

            <Divider t={t} />

            {/* Search */}
            <form
              onSubmit={handleSearchSubmit}
              className="relative flex-1 min-w-[180px] max-w-[300px]"
            >
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: searchQuery ? 0.8 : 0.4,
                  color: searchQuery ? t.activeText : undefined,
                }}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim())
                    handleSearchSubmit(e);
                  if (e.key === "Escape") clearSearch();
                }}
                placeholder={`Search ${activeViewLabel.toLowerCase()}...`}
                className="w-full pl-8 pr-7 py-2 rounded-lg text-sm outline-none transition-colors"
                style={{
                  background: t.inputBg,
                  border: searchQuery
                    ? `1px solid ${t.hoverBorder}`
                    : `1px solid ${t.inputBorder}`,
                  color: t.inputText,
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-80 transition-opacity"
                  onClick={clearSearch}
                >
                  <X size={14} />
                </button>
              )}
            </form>

            <Divider t={t} />

            {/* Sort */}
            <DropUp
              isOpen={sortOpen}
              onClose={() => setSortOpen(false)}
              t={t}
              trigger={
                <button
                  onClick={() => setSortOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200"
                  style={{
                    background:
                      activeSort !== "newest" ? t.activeBg : t.inputBg,
                    border:
                      activeSort !== "newest"
                        ? `1px solid ${t.activeBorder}`
                        : `1px solid ${t.inputBorder}`,
                    color:
                      activeSort !== "newest" ? t.activeText : t.textSecondary,
                    cursor: "pointer",
                    minWidth: 130,
                  }}
                  onMouseEnter={(e) => {
                    if (activeSort === "newest") {
                      e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
                      e.currentTarget.style.boxShadow = t.hoverBtnShadow;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSort === "newest") {
                      e.currentTarget.style.border = `1px solid ${t.inputBorder}`;
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  <ArrowUpDown
                    size={14}
                    style={{ opacity: 0.6, flexShrink: 0 }}
                  />
                  <span
                    className="truncate flex-1 text-left"
                    style={{ fontSize: 12 }}
                  >
                    {SORT_OPTIONS.find((s) => s.key === activeSort)?.label}
                  </span>
                  <ChevronUp
                    size={14}
                    style={{
                      opacity: 0.4,
                      flexShrink: 0,
                      transform: sortOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.15s ease",
                    }}
                  />
                </button>
              }
            >
              {SORT_OPTIONS.map((opt) => (
                <DropUpItem
                  key={opt.key}
                  selected={activeSort === opt.key}
                  t={t}
                  onClick={() => {
                    setActiveSort(opt.key);
                    setSortOpen(false);
                  }}
                >
                  {opt.label}
                </DropUpItem>
              ))}
            </DropUp>

            <Divider t={t} />

            {/* Sources */}
            <MultiSelectDropdown
              isOpen={sourcesOpen}
              onClose={(val) => {
                if (val instanceof Set) {
                  handleSourcesChanged(val);
                } else {
                  setSourcesOpen(false);
                  setSourceSearch("");
                }
              }}
              onToggle={() => {
                setSourcesOpen((v) => !v);
                setSortOpen(false);
                if (sourcesOpen) setSourceSearch("");
              }}
              selected={selectedSources}
              options={availableSources}
              searchValue={sourceSearch}
              onSearchChange={setSourceSearch}
              label="Sources"
              icon={Rss}
              t={t}
              favoritedSources={favoritedSources}
              onToggleFavorite={handleToggleFavoriteSource}
            />

            <Divider t={t} />

            {/* Date Range */}
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
              }}
              t={t}
            />

            {/* Clear all */}
            {hasActiveFilter && (
              <>
                <Divider t={t} />
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{
                    color: t.clearText,
                    background: t.clearBg,
                    border: `1px solid ${t.clearBorder}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.clearBgHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = t.clearBg;
                  }}
                  onClick={clearAllFilters}
                >
                  <X size={12} />
                  Clear
                </button>
              </>
            )}

            <div className="flex-1" />
          </div>

          {/* ── Row 2: Filter pills ── */}
          <div
            className="mx-2.5"
            style={{ height: 1, background: t.divider, opacity: 0.4 }}
          />
          <div
            className="flex items-center gap-1.5 px-2.5 pb-3 pt-2 overflow-x-auto rounded-b-xl"
            style={{ scrollbarWidth: "none" }}
          >
            {FILTER_OPTIONS.map((opt) => {
              const isActive = activeFilter === opt.key;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs whitespace-nowrap transition-all duration-200 flex-shrink-0"
                  style={{
                    background: isActive ? t.activeBg : t.pillBg,
                    border: isActive
                      ? `1px solid ${t.activeBorder}`
                      : `1px solid ${t.pillBorder}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = t.hoverBg;
                      e.currentTarget.style.border = `1px solid ${t.hoverBorder}`;
                      e.currentTarget.style.boxShadow = t.hoverShadow;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = t.pillBg;
                      e.currentTarget.style.border = `1px solid ${t.pillBorder}`;
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                  onClick={() => setActiveFilter(opt.key)}
                >
                  <Icon
                    size={12}
                    style={{
                      color: isActive ? t.activeIcon : t.pillIconColor,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: isActive ? t.activeText : t.pillText,
                    }}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}

            <div className="flex-1" />

            {activeView?.kind !== "custom" && (
              <StatsBar stats={effectiveStats} t={t} />
            )}

            <IconBtn title="Refresh" onClick={handleRefresh} t={t}>
              <RotateCw size={16} style={{ color: t.iconDefault }} />
            </IconBtn>
          </div>
        </div>
      </div>

      {/* ═══════════ FEED CONTENT ═══════════ */}
      {isLoading ? (
        <div className="w-full flex-1 flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <div
          ref={feedScrollRef}
          className={cn(
            "flex-1 overflow-y-auto",
            "px-4 sm:px-6 lg:px-8",
            "pb-8",
          )}
          style={{ paddingTop: barHeight ? barHeight + 72 : 180 }}
        >
          {articleCards.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <p className="text-sm font-medium" style={{ color: t.textMuted }}>
                No articles found
              </p>
            </div>
          ) : (
            <div
              className={cn(
                "grid gap-5",
                "grid-cols-[repeat(auto-fill,_minmax(320px,_1fr))]",
                "justify-center",
              )}
            >
              {articleCards.map(({ id, article }) => (
                <FeedCard
                  key={id}
                  id={id}
                  article={article}
                  t={t}
                  onCardClick={handleCardClick}
                  onCardInteract={handleInteract}
                  isActive={activeCardId === id}
                  typeBadge={
                    <>
                      <TypeBadge
                        type={article.target_type || article.type}
                        t={t}
                      />
                      {article.target && (
                        <TargetBadge target={article.target} t={t} />
                      )}
                      {article.apt_name && (
                        <AptBadge aptName={article.apt_name} t={t} />
                      )}
                    </>
                  }
                />
              ))}
            </div>
          )}

          {articleCards.length > 0 && (
            <ScrollSentinel
              onIntersect={loadMore}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
            />
          )}
        </div>
      )}

      {/* ═══════════ CREATE / EDIT FEED VIEW MODAL ═══════════ */}
      {showCreateViewModal && (
        <CreateEditViewModal
          dark={dark}
          t={t}
          editingView={editingView}
          onClose={resetModal}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};

export default MainFeeds;
